import { css, cx } from "@linaria/core"
import * as Select from "@radix-ui/react-select"


import { ButtonStyles } from "@/components/Button/ButtonStyles.ts"
import { INPUT_HEIGHT_PX, INPUT_PADDING_PX } from "@/components/Input/Input.tsx"
import { dropdownShadow } from "@/styles/mixins.ts"

import type { ComponentPropsWithoutRef, ComponentRef, Ref } from "react"

const SelectRoot = Select.Root

const SelectPortal = Select.Portal

type SelectTriggerProps = ComponentPropsWithoutRef<typeof Select.Trigger> & {
  ref?: Ref<ComponentRef<typeof Select.Trigger>>
}

const SelectTrigger = ({ children, className, ref, ...props }: SelectTriggerProps) => (
  <Select.Trigger
    ref={ref}
    className={cx(ButtonStyles.Base, ButtonStyles.Kind.Alternative, ButtonStyles.Size.Large, styles.SelectTrigger, className)}
    {...props}
  >
    {children}
  </Select.Trigger>
)

const SelectValue = Select.Value

type SelectIconProps = ComponentPropsWithoutRef<typeof Select.Icon> & {
  ref?: Ref<ComponentRef<typeof Select.Icon>>
}

const SelectIcon = ({ children, className, ref, ...props }: SelectIconProps) => (
  <Select.Icon
    ref={ref}
    className={cx(styles.SelectIcon, className)}
    {...props}
  >
    {children}
  </Select.Icon>
)

type SelectContentProps = ComponentPropsWithoutRef<typeof Select.Content> & {
  ref?: Ref<ComponentRef<typeof Select.Content>>
}

const SelectContent = ({ children, className, ref, ...props }: SelectContentProps) => (
  <Select.Content
    ref={ref}
    className={cx(styles.SelectContent, className)}
    sideOffset={4}
    position="popper"
    {...props}
  >
    {children}
  </Select.Content>
)

const SelectViewport = Select.Viewport

type SelectItemProps = ComponentPropsWithoutRef<typeof Select.Item> & {
  ref?: Ref<ComponentRef<typeof Select.Item>>
}

const SelectItem = ({ children, className, ref, ...props }: SelectItemProps) => (
  <Select.Item
    ref={ref}
    className={cx(styles.SelectItem, className)}
    {...props}
  >
    {children}
  </Select.Item>
)

const SelectItemText = Select.ItemText

const styles = {
  SelectTrigger: css`
    /* It must be more specific than the button styles, which are used alongside */
    && {
      display: flex;
      align-items: center;
      gap: 16px;
    }
  `,

  SelectIcon: css`
    display: flex;
    color: var(--secondary-text-color);

    [disabled] & {
      opacity: 0.5;
    }
  `,

  SelectContent: css`
    overflow: hidden;
    min-width: var(--radix-select-trigger-width);

    border: 1px solid var(--separator-color);
    border-radius: var(--border-radius-20);

    background-color: var(--island-background-color);

    ${dropdownShadow};
  `,

  SelectItem: css`
    height: ${INPUT_HEIGHT_PX}px;
    padding: 0 ${INPUT_PADDING_PX}px;
    cursor: pointer;
    outline: none;

    &[data-state="checked"] {
      background-color: var(--text-color-10);
    }

    &:hover:not([data-state="checked"]) {
      background-color: var(--text-color-5);
    }

    &[data-highlighted]:not([data-state="checked"]) {
      background-color: var(--text-color-5);
    }
  `
}

export {
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
}